import {Button, Col, DatePicker, Form, Input, Row, Select, Radio, message} from "antd";
import React, { useEffect, useState } from "react";
import {
    addStagingPlod,
    fetchAllContract,
    fetchSelectOptionsByContractNO, updateStagingPlod,
} from "../../backendInteraction/ApiFetch";
import { DDRDisplayData } from "../../types/DDRDisplayData";
import dayjs from "dayjs";
import "./DDRPlodForm.css";
import SubTableManager from "../DDRSubTable/SubTableManager/SubTableManager";
import {useNavigate} from "react-router-dom";
import {depthValueValidator, formatTotalActivityHours} from "../../utils/Validator";
import {fetchTotalActivityHours, fetchTotalDrillingHours} from "../../backendInteraction/ActivitiesApiFetch";
import {fetchTotalDrillingMetresAndCoreLost} from "../../backendInteraction/DrillingApiFetch";
import {fetchTotalPersonnelNo} from "../../backendInteraction/PersonnelApiFetch";

const { Option } = Select;

interface DDRPlodFormProps {
    ddr: DDRDisplayData | null;
    aid: number;
    authorityLv: string;
}

const DDRPlodForm: React.FC<DDRPlodFormProps> = ({ ddr, aid , authorityLv  }) => {

    const [form] = Form.useForm();

    const [shift, setShift] = useState<string | null>(ddr?.shift || "Day");
    const [contractOptions, setContractOptions] = useState<{ value: string; label: string }[]>([]);
    const [rigOptions, setRigOptions] = useState<{ value: string; label: string }[]>([]);
    const [departmentOptions, setDepartmentOptions] = useState<{ value: string; label: string }[]>([]);
    const [selectedContract, setSelectedContract] = useState<string | null>(null);
    const [selectedRig, setSelectedRig] = useState<string | null>(null);
    const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
    const [totalActivityHours, setTotalActivityHours] = useState<number>(ddr?.total_activity_hours ?? 0);

    const [totalDrillingHours, setTotalDrillingHours] = useState<number>( 0);
    const [totalDrillingMetres, setTotalDrillingMetres] = useState<number>(0);
    const [totalCoreLost, setTotalCoreLost] = useState<number>(0);
    const [totalPersonnelNo, setTotalPersonnelNo] = useState<number>(0);

    const [currentDDR, setCurrentDDR] = useState<DDRDisplayData | null>(null);

    const [contractNo, setContractNoState] = useState<string>('');
    const [rigNo, setRigNoState] = useState<string>('');
    const [department, setDepartmentState] = useState<string>('');
    const [location, setLocationState] = useState<string>('');
    const [machHrs, setMachHrsState] = useState<string>('');

    const [isView, setIsView] = useState<boolean>(false);

    const navigate = useNavigate();

    useEffect(() => {
        const init = async () => {
            if (ddr) {
                setIsView(ddr.report_state % 2 > 0);
                setCurrentDDR(ddr);
                setSelectedContract(ddr.contract_no);

                const hours =  await fetchTotalActivityHours(ddr.pid, false, aid);
                const drillingHours = await fetchTotalDrillingHours(ddr.pid, aid);
                const { totalMetres, totalCoreLost } = await fetchTotalDrillingMetresAndCoreLost(ddr.pid, aid);
                const personnelNo = await fetchTotalPersonnelNo(ddr.pid, aid);

                setTotalActivityHours(hours);
                setTotalDrillingHours(drillingHours);
                setTotalDrillingMetres(totalMetres);
                setTotalCoreLost(totalCoreLost);
                setTotalPersonnelNo(personnelNo);

                form.setFieldsValue({
                    contractNo: ddr.contract_no,
                    rigNo: ddr.rig_no,
                    date: ddr.date
                        ? dayjs(ddr.date.replace(/\b[a-z]/g, char => char.toUpperCase()), "DD-MMM-YY", true)
                        : null,
                    shift: ddr.shift,
                    department: ddr.department,
                    location: ddr.location,
                    mach_hrs_from: ddr.mach_hrs_from,
                    mach_hrs_to: ddr.mach_hrs_to,
                    total_activity_hours: hours,
                    comments: ddr.comments,
                });
            } else {
                const hours =  await fetchTotalActivityHours(-1, true, aid);
                const drillingHours = await fetchTotalDrillingHours(-1, aid);
                const { totalMetres, totalCoreLost } = await fetchTotalDrillingMetresAndCoreLost(-1, aid);
                const personnelNo = await fetchTotalPersonnelNo(-1, aid);

                setTotalActivityHours(hours);
                setTotalDrillingHours(drillingHours);
                setTotalDrillingMetres(totalMetres);
                setTotalCoreLost(totalCoreLost);
                setTotalPersonnelNo(personnelNo);

                const storedContract = localStorage.getItem('DORS_CONTRACT_NO');
                const storedRig = localStorage.getItem('DORS_RIG_NO');
                const storedDept = localStorage.getItem('DORS_DEPARTMENT');
                const storedLoc = localStorage.getItem('DORS_LOCATION');
                const storedMachHrs = localStorage.getItem('DORS_MACH_HRS');

                setSelectedContract(storedContract || null);
                setSelectedRig(storedRig || null);
                setSelectedDepartment(storedDept || null);

                const now = new Date();
                const defaultShift = now.getHours() >= 6 && now.getHours() < 18 ? "Day" : "Night";
                const formattedDate = `${String(now.getDate()).padStart(2, "0")}-${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][now.getMonth()]}-${String(now.getFullYear()).slice(-2)}`;

                form.setFieldsValue({
                    contractNo: storedContract || null,
                    rigNo: storedRig || null,
                    department: storedDept || null,
                    location: storedLoc || null,
                    mach_hrs_from: storedMachHrs || null,
                    shift: defaultShift,
                    date: dayjs(formattedDate, "DD-MMM-YY"),
                });
            }
        };

        init();
    }, [ddr, form]);


    useEffect(() => {
        const loadContracts = async () => {
            const contracts = await fetchAllContract();
            setContractOptions(contracts.map(contract => ({ value: contract, label: contract })));
        };

        loadContracts();
    }, []);

    useEffect(() => {
        const loadRigNumbers = async () => {
            if (selectedContract) {
                const rigs = await fetchSelectOptionsByContractNO(selectedContract, "RigNo");
                setRigOptions(rigs.map(rig => ({ value: rig, label: rig })));
            } else {
                setRigOptions([]);
            }
        };

        const loadDepartments = async () => {
            if (selectedContract) {
                const departments = await fetchSelectOptionsByContractNO(selectedContract, "Department");
                setDepartmentOptions(departments.map(dep => ({ value: dep, label: dep })));
            } else {
                setDepartmentOptions([]);
            }
        };

        loadRigNumbers();
        loadDepartments();
    }, [selectedContract]);

    const setContractNo = async (value: string) => {
        setContractNoState(value);
        localStorage.setItem('DORS_CONTRACT_NO', value);
    };

    const setRigNo = async (value: string) => {
        setRigNoState(value);
        localStorage.setItem('DORS_RIG_NO', value);
    };

    const setDepartment = async (value: string) => {
        setDepartmentState(value);
        localStorage.setItem('DORS_DEPARTMENT', value);
    };

    const setLocation = async (value: string) => {
        setLocationState(value);
        localStorage.setItem('DORS_LOCATION', value);
    };

    const setMachHrs = async (value: string) => {
        setMachHrsState(value);
        localStorage.setItem('DORS_MACH_HRS', value);
    };

    const onFinish = (values: any) => {
        console.log("Form values before saving:", values);

        if (!(authorityLv && aid))
        {
            console.log('account level : ', authorityLv, ', account : ', aid);
            alert("Submit Failed, you need to login first.");
            return;
        }

        if (isView) {
            if (authorityLv === 'A')
            {
                message.info('DDR editing is only available for drillers and supervisors.');
                return;
            }
            setIsView(false);
            return;
        }

        if (!values.contractNo || !values.rigNo) {
            message.error("Contract Number and Rig Number cannot be empty!");
            return;
        }

        if (!values.comments) {
            message.info("Don’t forget to leave a comment!");
            return;
        }

        if ( !depthValueValidator(values.mach_hrs_from, values.mach_hrs_to, true)) return;

        if ( parseFloat(values.mach_hrs_to) -  parseFloat(values.mach_hrs_from) > 12) {
            message.error("Total Machine hours duration exceeds 12 hours!");
            return;
        }

        const currentDate = new Date();
        const formattedDate = `${String(currentDate.getDate()).padStart(2, "0")}-${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][currentDate.getMonth()]}-${String(currentDate.getFullYear()).slice(-2)}`;
        const defaultShift = currentDate.getHours() >= 6 && currentDate.getHours() < 18 ? "Day" : "Night";

        const updatedDDR: DDRDisplayData = currentDDR
            ? {
                ...currentDDR,
                contract_no: values.contractNo,
                rig_no: values.rigNo,
                date: values.date ? values.date.format("DD-MMM-YY") : currentDDR.date,
                shift: values.shift || currentDDR.shift,
                creation_date: '',
                department: values.department || '',
                location: values.location || '',
                mach_hrs_from: values.mach_hrs_from || '',
                mach_hrs_to: values.mach_hrs_to || '',
                total_activity_hours: totalActivityHours || 0,
                comments: values.comments || '',
            }
            : {
                key: -1,
                pid: -1,
                contract_no: values.contractNo,
                rig_no: values.rigNo,
                date: values.date ? values.date.format("DD-MMM-YY") : formattedDate,
                shift: values.shift || defaultShift,
                creation_date: '',
                department: values.department || '',
                location: values.location || '',
                mach_hrs_from: values.mach_hrs_from || '',
                mach_hrs_to: values.mach_hrs_to || '',
                total_activity_hours: totalActivityHours || 0,
                comments: values.comments || '',
                report_state: -1,
            };

        !(updatedDDR.contract_no === contractNo) && setContractNo(updatedDDR.contract_no);
        !(updatedDDR.rig_no === rigNo) && setRigNo(updatedDDR.rig_no);
        !(updatedDDR.department === department) && setDepartment(updatedDDR.department);
        !(updatedDDR.location === location) && setLocation(updatedDDR.location);
        !(updatedDDR.mach_hrs_to === machHrs) && setMachHrs(updatedDDR.mach_hrs_to);

        if (updatedDDR.pid === -1) {
            addStagingPlod(updatedDDR, aid, authorityLv)
                .then(result => console.log("Update successful:", result))
                .catch(error => console.error("Failed to update:", error));
            setSelectedContract(null);
            setSelectedRig(null);
            setSelectedDepartment(null);
            form.resetFields();
            navigate("/ddr-2-approve");
            return;
        }

        updateStagingPlod(updatedDDR)
            .then(result => console.log("Update successful:", result))
            .catch(error => console.error("Failed to update:", error));

        setCurrentDDR(updatedDDR);
        setIsView(true);
        console.log("Updated DDR Data:", updatedDDR);
    };

    return (
        <Form form={form} className="ddr_plod_form" name="ddr_plod_form" layout="horizontal" colon={false} onFinish={onFinish}>
            <h2 className="form-title">
                <span className='ddr_title_initial'>{`D`}</span>
                <span>{`AILY `}</span>
                <span className='ddr_title_initial'>{`D`}</span>
                <span>{`RILL `}</span>
                <span className='ddr_title_initial'>{`R`}</span>
                <span>{`EPORT`}</span>
            </h2>

            <Row gutter={16}>
                <Col span={6}>
                    { isView ?
                        (<div className="ddr_view_cell">
                            <p>
                                <span className="ddr_label">{`Contract Number: `}</span>
                                <span className="ddr_value">{currentDDR?.contract_no}</span>
                            </p>
                        </div>) : (
                            <Form.Item label="Contract Number" name="contractNo" labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <Select
                                    showSearch
                                    allowClear={true}
                                    options={contractOptions}
                                    placeholder=""
                                    filterOption={(input, option) =>
                                        option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                                    }
                                    onChange={value => setSelectedContract(value)}
                                />
                            </Form.Item>)}
                </Col>
                <Col span={6}>
                    { isView ?
                        (<div className="ddr_view_cell">
                            <p>
                                <span className="ddr_label">{`Rig Number: `}</span>
                                <span className="ddr_value">{currentDDR?.rig_no}</span>
                            </p>
                        </div>) : (
                            <Form.Item label="Rig Number" name="rigNo" labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <Select
                                    showSearch
                                    allowClear={true}
                                    options={rigOptions}
                                    placeholder=""
                                    filterOption={(input, option) =>
                                        option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                                    }
                                    onChange={value => setSelectedRig(value)}
                                />
                            </Form.Item>)}
                </Col>
                <Col span={6}>
                    { isView ?
                        (<div className="ddr_view_cell">
                            <p>
                                <span className="ddr_label">{`Plod Date: `}</span>
                                <span className="ddr_value">{currentDDR?.date}</span>
                            </p>
                        </div>) : (
                            <Form.Item label="Date" name="date" labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <DatePicker placeholder="" format="DD-MMM-YY" style={{ width: "100%"}} />
                            </Form.Item>)}
                </Col>
                <Col span={6}>
                    { isView ?
                        (<div className="ddr_view_cell">
                            <p>
                                <span className="ddr_label">{`Plod Shift: `}</span>
                                <span className="ddr_value">{currentDDR?.shift}</span>
                            </p>
                        </div>) : (
                            <Form.Item label="Shift" name="shift" labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <Radio.Group value={shift} onChange={e => setShift(e.target.value)}>
                                    <Radio value="Day">Day</Radio>
                                    <Radio value="Night">Night</Radio>
                                </Radio.Group>
                            </Form.Item>)}
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={6}>
                    { isView ?
                        (<div className="ddr_view_cell">
                            <p>
                                <span className="ddr_label">{`Machine Hours From: `}</span>
                                <span className="ddr_value">{currentDDR?.mach_hrs_from || 'No Data'}</span>
                            </p>
                        </div>) : (
                            <Form.Item label="Machine Hrs From" name="mach_hrs_from" labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <Input className="ddr_form_input" allowClear={true}/>
                            </Form.Item>)}
                </Col>
                <Col span={6}>
                    { isView ?
                        (<div className="ddr_view_cell">
                            <p>
                                <span className="ddr_label">{`Machine Hours To: `}</span>
                                <span className="ddr_value">{currentDDR?.mach_hrs_to || 'No Data'}</span>
                            </p>
                        </div>) : (
                            <Form.Item label="Machine Hrs To" name="mach_hrs_to" labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <Input className="ddr_form_input" allowClear={true}/>
                            </Form.Item>)}
                </Col>
                <Col span={6}>
                    { isView ?
                        (<div className="ddr_view_cell">
                            <p>
                                <span className="ddr_label">{`Department: `}</span>
                                <span className="ddr_value">{currentDDR?.department || 'No Data'}</span>
                            </p>
                        </div>) : (
                            <Form.Item label="Department" name="department" labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <Select
                                    showSearch
                                    allowClear={true}
                                    options={departmentOptions}
                                    placeholder=""
                                    filterOption={(input, option) =>
                                        option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                                    }
                                    onChange={value => setSelectedDepartment(value)}
                                />
                            </Form.Item>)}
                </Col>
                <Col span={6}>
                    { isView ?
                        (<div className="ddr_view_cell">
                            <p>
                                <span className="ddr_label">{`Location: `}</span>
                                <span className="ddr_value">{currentDDR?.location || 'No Data'}</span>
                            </p>
                        </div>) : (
                            <Form.Item label="Location" name="location" labelCol={{ span: 10 }} wrapperCol={{ span: 14 }}>
                                <Input className="ddr_form_input" allowClear={true}/>
                            </Form.Item>)}
                </Col>
            </Row>

            <Row gutter={20}>
                <Col span={4}>
                    <div className= {isView ? "ddr_view_cell" : "ddr_edit_cell"}>
                        <p>
                            <span className={ totalActivityHours < 11.8 ? 'total_activity_hrs_red'
                                : totalActivityHours > 12.2 ? 'total_activity_hrs_yellow' : 'total_activity_hrs_green' }>
                                {`Total Activity Hours: `}
                            </span>
                            <span className="ddr_value">{formatTotalActivityHours(totalActivityHours)}</span>
                        </p>
                    </div>
                </Col>
                <Col span={4}>
                    <div className= {isView ? "ddr_view_cell" : "ddr_edit_cell"}>
                        <p>
                            <span>
                                {`Total Drilling Hours: `}
                            </span>
                            <span className="ddr_value">{totalDrillingHours}</span>
                        </p>
                    </div>
                </Col>
                <Col span={4}>
                    <div className= {isView ? "ddr_view_cell" : "ddr_edit_cell"}>
                        <p>
                            <span>
                                {`Total Drilling Metres: `}
                            </span>
                            <span className="ddr_value">{totalDrillingMetres}</span>
                        </p>
                    </div>
                </Col>
                <Col span={4}>
                    <div className= {isView ? "ddr_view_cell" : "ddr_edit_cell"}>
                        <p>
                            <span>
                                {`Total Core Lost: `}
                            </span>
                            <span className="ddr_value">{totalCoreLost}</span>
                        </p>
                    </div>
                </Col>
                <Col span={4}>
                    <div className= {isView ? "ddr_view_cell" : "ddr_edit_cell"}>
                        <p>
                            <span>
                                {`Total Personnel: `}
                            </span>
                            <span className="ddr_value">{totalPersonnelNo}</span>
                        </p>
                    </div>
                </Col>
            </Row>

            <div className="ddr_sub_tables">
                <SubTableManager
                    ddr={ ddr || null }
                    contractNo={selectedContract ?? ''}
                    shift={shift ?? ''}
                    isView={ isView }
                    onTotalHoursUpdate={(total) => setTotalActivityHours(total)}
                    onTotalDrillingHoursUpdate={(total) => setTotalDrillingHours(total)}
                    onTotalDrillingMetresUpdate={(total) => setTotalDrillingMetres(total)}
                    onTotalCoreLostUpdate={(total) => setTotalCoreLost(total)}
                    onTotalPersonnelUpdate={(total) => setTotalPersonnelNo(total)}
                />
            </div>

            { isView ?
                (<div className="ddr_view_comments">
                    <p>
                        <span className="ddr_label">{`Comments: `}</span>
                        <span className="ddr_value">{currentDDR?.comments || 'No Data'}</span>
                    </p>
                </div>) : (
                    <Form.Item label="Comments" name="comments" className="comments" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
                        <Input.TextArea showCount rows={4} allowClear={true}/>
                    </Form.Item>)}

            <Row justify="center" gutter={16}>
                <Col><Button type="primary" style={{backgroundColor: '#002E57'}} htmlType="submit">{isView ? 'Edit' : 'Save'}</Button></Col>
                <Col><Button onClick={() => {
                    if (isView) {
                        navigate("/ddr-2-approve");
                    } else  {
                        setSelectedContract(null);
                        setSelectedRig(null);
                        setSelectedDepartment(null);
                        form.resetFields();

                        const now = new Date();
                        const formattedDate = `${String(now.getDate()).padStart(2, "0")}-${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][now.getMonth()]}-${String(now.getFullYear()).slice(-2)}`;

                        form.setFieldsValue({
                            shift: new Date().getHours() >= 6 && new Date().getHours() < 18 ? 'Day' : 'Night',
                            date: dayjs(formattedDate, "DD-MMM-YY"),
                        });
                    }
                }}>{isView ? 'Back' : 'Clear'}</Button></Col>
            </Row>
        </Form>
    );
};

export default DDRPlodForm;
